// UpsConnection.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    ChevronLeft,
    PackageSearch,
    Settings,
    Truck,
    Building2,
    Save,
    Link2,
    Info,
    Plus,
    Trash2,
    RotateCcw,
} from "lucide-react";
import Select, { components } from "react-select";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import TaxRuleSelect from "components/services/taxrulesandTaxgroupsui";
import AddressAutocomplete from "components/AddressAutocomplete";
/**
 * UPS Carrier Connection
 * 5 tabs with distinct colors, Bootstrap-first styling, react-select dropdowns (dummy data),
 * and a rich "Add New Package" flow that renders editable cards with delete.
 */

import { getAuthCookie } from "../../utils/authCookies";

type TabKey = "account" | "packages" | "carrier" | "shipment" | "shipper";

/* ----------------------------- TAB META ----------------------------- */
const TAB_META: Record<TabKey, { label: string; shortLabel: string; color: string; icon: React.ReactNode }> = {
    account: { label: "Account Information", shortLabel: "Account", color: "#155dfc", icon: <Settings size={16} className="me-2" /> },
    packages: { label: "Package Settings", shortLabel: "Packages", color: "#9810fa", icon: <PackageSearch size={16} className="me-2" /> },
    carrier: { label: "Shipping Carrier", shortLabel: "Carrier", color: "#00a63e", icon: <Truck size={16} className="me-2" /> },
    shipment: { label: "Shipment Settings", shortLabel: "Shipment", color: "#ec003f", icon: <Settings size={16} className="me-2" /> },
    shipper: { label: "Shipper Address", shortLabel: "Address", color: "#f54a00", icon: <Building2 size={16} className="me-2" /> },
};

/* ------------------------- REACT-SELECT UTILS ------------------------ */
const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    indicatorSeparator: () => ({ display: "none" }),
    control: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
        cursor: s.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
        borderWidth: 2,
        borderColor: "#c9ced6",
        minHeight: 44,
    }),
    singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    valueContainer: (p: any) => ({ ...p, padding: "6px 10px" }),
    input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
    placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
    multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
};

/* ----------------------------- TAB PILL ------------------------------ */
const TabButton: React.FC<{
    active: boolean;
    color: string;
    label: string;
    shortLabel: string;
    icon?: React.ReactNode;
    onClick: () => void;
}> = ({ active, color, label, shortLabel, icon, onClick }) => (
    <button
        type="button"
        className={`rc-tab ${active ? "rc-tab-active" : ""}`}
        style={{
            backgroundColor: active ? color : "#f8f9fb",
            borderColor: active ? color : "#e5e7eb",
            color: active ? "#fff" : "#374151",
        }}
        onClick={onClick}
    >
        <span className="rc-tab-icon d-none d-lg-inline">{icon}</span>
        <span className="rc-tab-label d-none d-lg-inline">{label}</span>
        <span className="rc-tab-short-label d-lg-none">{shortLabel}</span>
    </button>
);

/* ----------------------------- DUMMY DATA ---------------------------- */
const AUTH_MODES = ["OAuth (Client Secret)"];
const ENVIRONMENT_OPTIONS = [
    { value: "sandbox", label: "Test" },
    { value: "production", label: "Production" }
];
const YES_NO = ["Yes", "No"];
const PRICE_UNIT = ["%", "$"] as const;
const SIGN = ["+", "-"] as const;
const LABEL_FORMATS = [
    { value: "PDF", label: "Default PDF" },
    { value: "GIF", label: "GIF" },
    { value: "ZPL", label: "ZPL (Thermal Printer)" },
    { value: "EPL", label: "EPL (Thermal Printer)" },
    { value: "STARPL", label: "STARPL (Thermal Printer)" },
    { value: "SPL", label: "SPL (Thermal Printer)" }
];
const LABEL_LAYOUTS = [
    { value: "4x6", label: "Paper 4x6" },
    { value: "4x8", label: "Paper 4x8" }
];
const COUNTRIES = ["United States", "Canada", "India"];
const STATES_US = ["CA", "NY", "TX", "FL", "WA", "Other"];
const DUMMY_SERVICES = [
    { code: "ups_01", name: "Next Day Air" },
    { code: "ups_02", name: "2nd Day Air" },
    { code: "ups_03", name: "Ground" },
    { code: "ups_07", name: "Worldwide Express" },
    { code: "ups_08", name: "Worldwide Expedited" },
    { code: "ups_11", name: "Standard" },
    { code: "ups_12", name: "3 Day Select" },
    { code: "ups_13", name: "Next Day Air Saver" },
    { code: "ups_14", name: "Next Day Air Early" },
    { code: "ups_54", name: "Worldwide Express Plus" },
    { code: "ups_59", name: "2nd Day Air A.M." },
    { code: "ups_65", name: "Worldwide Saver" },
    { code: "ups_92", name: "SurePost < 1 lb" },
    { code: "ups_93", name: "SurePost 1 lb or Greater" },
    { code: "ups_94", name: "SurePost BPM" }
];

/* --------- Package Presets (rich grouped options like screenshot) -------- */
type PackagePreset = {
    value: string;
    label: string;
    code: string;
    name: string;
    dimsIn: { w: number | null; h: number | null; l: number | null };
    maxWeightLb: number | null;
};

const PACKAGE_PRESET_OPTIONS: Array<{ label: string; options: PackagePreset[] }> = [
    {
        label: "Envelopes & Paks",
        options: [
            { value: "ups_letter", label: "UPS Letter", code: "01", name: "UPS Letter", dimsIn: { w: 9.5, h: 13, l: 5 }, maxWeightLb: 1 },
            { value: "ups_tube", label: "UPS Tube", code: "03", name: "Tube", dimsIn: { w: 6, h: 6, l: 38 }, maxWeightLb: 30 },
            { value: "ups_pak", label: "UPS Pak", code: "04", name: "PAK", dimsIn: { w: 11, h: 13, l: 17 }, maxWeightLb: 30 },
        ],
    },
    {
        label: "Boxes",
        options: [
            { value: "ups_express_box", label: "UPS Express Box", code: "21", name: "UPS Express Box", dimsIn: { w: 11, h: 13, l: 2 }, maxWeightLb: 30 },
            { value: "ups_s_box", label: "UPS Small Express Box", code: "2a", name: "Small Express Box", dimsIn: { w: 11, h: 13, l: 2 }, maxWeightLb: 30 },
            { value: "ups_m_box", label: "UPS Medium Express Box", code: "2b", name: "Medium Express Box", dimsIn: { w: 11, h: 16, l: 3 }, maxWeightLb: 30 },
            { value: "ups_l_box", label: "UPS Large Express Box", code: "2c", name: "Large Express Box", dimsIn: { w: 13, h: 18, l: 3 }, maxWeightLb: 30 },
            { value: "ups_10kg", label: "UPS 10kg Box", code: "25", name: "UPS 10KG Box", dimsIn: { w: 16.5, h: 13.25, l: 10.75 }, maxWeightLb: 22 },
            { value: "ups_25kg", label: "UPS 25kg Box", code: "24", name: "UPS 25KG Box", dimsIn: { w: 17.75, h: 19.75, l: 13.25 }, maxWeightLb: 55 },
        ],
    },
    {
        label: "Other",
        options: [
            { value: "custom_pkg", label: "My Own Package", code: "02", name: "Customer Supplied Package", dimsIn: { w: 10, h: 10, l: 10 }, maxWeightLb: 70 },
            // { value: "pallet", label: "Pallet", code: "30", name: "Pallet", dimsIn: { w: null, h: null, l: null }, maxWeightLb: null },
        ],
    },
];

const PackageOption = (props: any) => {
    const { label, dimsIn, maxWeightLb } = props.data as PackagePreset;
    const dims =
        dimsIn.l !== null && dimsIn.w !== null && dimsIn.h !== null
            ? `${dimsIn.l}" × ${dimsIn.w}" × ${dimsIn.h}"`
            : "—";
    return (
        <components.Option {...props}>
            <div className="d-flex flex-column">
                <span className="fw-semibold">{label}</span>
                <small className="text-muted">{dims} · {maxWeightLb ?? "—"}lbs</small>
            </div>
        </components.Option>
    );
};

/* ------------------------------ TYPES -------------------------------- */
type ServiceRow = {
    code: string;
    name: string;
    enabled: boolean;
    sign: (typeof SIGN)[number];
    amount: string;
    unit: (typeof PRICE_UNIT)[number];
};

// API payload type for bulk service creation
type ServiceBulkPayload = {
    shipping_integration_id: string;
    service_code: string;
    service_name: string;
    enabled: boolean;
    adjustment_type: string;
    adjustment_value: number;
};

// API payload type for bulk service update
type ServiceBulkUpdatePayload = {
    _id: string;
    enabled: boolean;
    adjustment_type: string;
    adjustment_value: number;
};

// API payload type for bulk service delete
type ServiceBulkDeletePayload = string[];

// API response type for fetched shipping services
type FetchedShippingService = {
    _id: string;
    shipping_integration_id: string;
    service_code: string;
    service_name: string;
    enabled: boolean;
    adjustment_type: string;
    adjustment_value: number;
    created_by: {
        id: string;
        name: string;
    };
    updated_by: {
        id: string;
        name: string;
    };
    created_at: string;
    updated_at: string;
};

// UPS Shipment Settings types
type ShipmentSettingsPayload = {
    shipping_integration_id: string;
    label_format: string;
    label_layout: string;
};

type ShipmentSettingsResponse = {
    _id: string;
    shipping_integration_id: string;
    label_format: string;
    label_layout: string;
    created_by: {
        id: string;
        name: string;
    };
    updated_by: {
        id: string;
        name: string;
    };
    created_at: string;
    updated_at: string;
};

// UPS Shipper Address types
type ShipperAddressPayload = {
    shipping_integration_id: string;
    shipper_name: string;
    company_name: string;
    phone_number: string;
    email_address: string;
    address_line1: string;
    address_line2: string;
    city: string;
    city_code: string;
    state: string;
    state_code: string;
    country: string;
    country_code: string;
    postal_code: string;
    label_format: string;
    label_layout: string;
};

type ShipperAddressResponse = {
    _id: string;
    shipping_integration_id: string;
    shipper_name: string;
    company_name: string;
    phone_number: string;
    email_address: string;
    address_line1: string;
    address_line2: string;
    city: string;
    city_code: string;
    state: string;
    state_code: string;
    country: string;
    country_code: string;
    postal_code: string;
    label_format: string;
    label_layout: string;
    created_by: {
        id: string;
        name: string;
    };
    updated_by: {
        id: string;
        name: string;
    };
    created_at: string;
    updated_at: string;
};

// UPS Package Options types
type PackageOptionsPayload = {
    shipping_integration_id: string;
    use_multiple_packages: boolean;
};

type PackageOptionsResponse = {
    _id?: string;
    id?: string;
    shipping_integration_id: string;
    use_multiple_packages: boolean;
    created_by: {
        id: string;
        name: string;
    };
    updated_by: {
        id: string;
        name: string;
    };
    created_at: string;
    updated_at: string;
};

type PackageCard = {
    id: string;
    presetValue: string;
    displayName: string;
    packageName: string;  // Custom name field for user input
    code: string;
    name: string;
    width: number | "";
    height: number | "";
    length: number | "";
    maxWeight: number | "";
    additionalWeight: number | "";
};

/* ============================== COMPONENT ============================= */
const UpsConnection: React.FC = () => {
    const [active, setActive] = useState<TabKey>("account");
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    // Determine if this is add or edit mode
    const isEdit = !!id;
    const isAdd = !isEdit;

    // Get user info from localStorage
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");

    /* -------- Account -------- */
    const [authMode, setAuthMode] = useState(AUTH_MODES[0]);
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [environment, setEnvironment] = useState("sandbox");
    const [upsStatus, setUpsStatus] = useState(false);
    const [accountTried, setAccountTried] = useState(false);
    const [upsConfigId, setUpsConfigId] = useState<string>(""); // Store UPS config ID from API response
    // Taxable + Tax Rule (UPS, controlled — mirrors Shipping via tiny adapter)
    const [isTaxable, setIsTaxable] = useState<boolean>(true);
    const [taxState, setTaxState] = useState<{
        tax_rule_id: string;   // optional: if you store pure id from UI
        tax_group: any | null; // selected group object (or null)
        tax_rule: any | null;  // selected rule object (or null)
    }>({ tax_rule_id: "", tax_group: null, tax_rule: null });

    // minimal Formik-like adapter so we can reuse TaxRuleSelect unchanged
    const taxFormik = useMemo(() => ({
        values: taxState,
        setFieldValue: (name: string, value: any) =>
            setTaxState((s) => ({ ...s, [name]: value })),
        handleBlur: () => { },
    }), [taxState]);

    // Loading states
    const [loading, setLoading] = useState({
        save: false,
        fetch: false,
        connect: false
    });

    // Error state
    const [error, setError] = useState<string>("");

    /* -------- Package Settings -------- */
    const [useMulti, setUseMulti] = useState(YES_NO[0]);
    const [newPkgType, setNewPkgType] = useState<string>("");
    const [packageCards, setPackageCards] = useState<PackageCard[]>([]);
    const allPresetsFlat = useMemo(
        () => PACKAGE_PRESET_OPTIONS.flatMap((g) => g.options),
        []
    );

    /* -------- Services (Carrier) -------- */
    const initialServices: ServiceRow[] = useMemo(
        () =>
            DUMMY_SERVICES.map((s) => ({
                code: s.code,
                name: s.name,
                enabled: false, // All services disabled by default
                sign: "+",
                amount: "",
                unit: "%",
            })),
        []
    );
    const [services, setServices] = useState<ServiceRow[]>(initialServices);

    // Track fetched services for comparison (to determine create/update/delete operations)
    const [fetchedServices, setFetchedServices] = useState<FetchedShippingService[]>([]);

    /* -------- Shipment Settings -------- */
    const [shipLabelFormat, setShipLabelFormat] = useState<string>("");
    const [shipLabelLayout, setShipLabelLayout] = useState<string>("");
    const [shipmentSettingsId, setShipmentSettingsId] = useState<string>(""); // Store shipment settings ID
    const [fetchedShipperAddresses, setFetchedShipperAddresses] = useState<ShipperAddressResponse[]>([]);
    const [currentShipperAddressId, setCurrentShipperAddressId] = useState<string>(""); // Store current shipper address ID
    const [fetchedPackageOptions, setFetchedPackageOptions] = useState<PackageOptionsResponse[]>([]);
    const [currentPackageOptionsId, setCurrentPackageOptionsId] = useState<string>(""); // Store current package options ID
    const [useMultiplePackages, setUseMultiplePackages] = useState<boolean>(false);

    /* -------- Shipper Address -------- */
    const [shipper, setShipper] = useState({
        name: "",
        company: "",
        phone: "",
        email: "",
        addr1: "",
        addr2: "",
        city: "",
        state: "",
        postal: "",
        country: "",
        labelFormat: "",
        labelLayout: "",
        cityCode: "",
        stateCode: "",
        countryCode: "",
        latitude: 0,
        longitude: 0,
    });

    const handleShipperAddr1Change = (value: string) => {
        setShipper(prev => ({
            ...prev,
            addr1: value,
            ...(!value.trim()
                ? {
                    city: "",
                    cityCode: "",
                    state: "",
                    stateCode: "",
                    postal: "",
                    country: "",
                    countryCode: "",
                    latitude: 0,
                    longitude: 0,
                }
                : {}),
        }));
    };

    useEffect(() => {
        document.querySelector("#ups-connection-top")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [active]);

    /* ---------------------- Package Cards Handlers ---------------------- */
    const newLocalPackageId = () =>
        `own-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`}`;

    const handleAddPackageCard = (presetValue: string) => {
        const preset = allPresetsFlat.find((p) => p.value === presetValue);
        if (!preset) return;

        // Multiple "My Own Package" cards are added via the Own Package button when multi-shipment is No.
        if (presetValue !== "custom_pkg") {
            const existingCard = packageCards.find((card) => card.presetValue === presetValue);
            if (existingCard) return;
        }

        const id =
            presetValue === "custom_pkg" ? newLocalPackageId() : preset.code;
        setPackageCards((cards) => [
            ...cards,
            {
                id,
                presetValue: preset.value,
                displayName: preset.label,
                packageName: preset.label, // Initialize with preset label, user can edit
                code: preset.code,
                name: preset.name,
                width: preset.dimsIn.w ?? "",
                height: preset.dimsIn.h ?? "",
                length: preset.dimsIn.l ?? "",
                maxWeight: preset.maxWeightLb ?? "",
                additionalWeight: 0,
            },
        ]);
    };

    /** When "Use multiple-package shipments" is No and type is My Own Package: add another custom card per click. */
    const addOwnPackageCard = () => {
        const preset = allPresetsFlat.find((p) => p.value === "custom_pkg");
        if (!preset) return;
        const id = newLocalPackageId();
        setPackageCards((cards) => [
            ...cards,
            {
                id,
                presetValue: preset.value,
                displayName: preset.label,
                packageName: preset.label,
                code: preset.code,
                name: preset.name,
                width: preset.dimsIn.w ?? "",
                height: preset.dimsIn.h ?? "",
                length: preset.dimsIn.l ?? "",
                maxWeight: preset.maxWeightLb ?? "",
                additionalWeight: 0,
            },
        ]);
    };

    const removePackageCard = async (cardId: string) => {
        // Check if this is a fetched package (has API ID) or local package
        // Fetched packages have MongoDB ObjectId format (24 chars) or start with 'fetched-'
        // Local packages use UPS package codes (like "01", "21", "2a", etc.)
        const isFetchedPackage = cardId.startsWith('fetched-') || cardId.length === 24;

        if (isFetchedPackage) {
            // Delete from API
            try {
                setLoading(prev => ({ ...prev, save: true }));
                setError("");

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/shipping/ups/packages/${cardId}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || "Failed to delete package");
                }

                showSuccess("Package deleted successfully!");
                // navigate(0);

                // Remove from local state
                setPackageCards((cards) => cards.filter((c) => c.id !== cardId));

            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Failed to delete package";
                setError(errorMessage);
                showError(errorMessage);
            } finally {
                setLoading(prev => ({ ...prev, save: false }));
            }
        } else {
            // For local packages (not yet saved), just remove from state
            setPackageCards((cards) => cards.filter((c) => c.id !== cardId));
        }
    };

    const updateCard = (id: string, key: keyof PackageCard, value: string) => {
        setPackageCards((cards) =>
            cards.map((c) =>
                c.id === id ? {
                    ...c,
                    [key]: (key === "packageName" || key === "presetValue" || key === "displayName" || key === "code" || key === "name")
                        ? value
                        : (value === "" ? "" : Number(value))
                } : c
            )
        );
    };

    /* -------------------------- Service Helpers ------------------------- */
    const updateService = <K extends keyof ServiceRow,>(
        code: string,
        key: K,
        value: ServiceRow[K]
    ) => {
        setServices((rows) => rows.map((r) => (r.code === code ? { ...r, [key]: value } : r)));
    };

    // Analyze changes and determine bulk operations needed
    const analyzeServiceChanges = () => {
        const currentEnabledServices = services.filter(service => service.enabled);
        const currentDisabledServices = services.filter(service => !service.enabled);

        // Ensure fetchedServices is an array to prevent null/undefined errors
        const safeFetchedServices = Array.isArray(fetchedServices) ? fetchedServices : [];

        // Services to create (newly enabled, not in fetched services)
        const servicesToCreate: ServiceBulkPayload[] = [];

        // Services to update (enabled in both current and fetched, but values changed)
        const servicesToUpdate: ServiceBulkUpdatePayload[] = [];

        // Services to delete (was enabled in fetched, now disabled in current)
        const servicesToDelete: string[] = [];

        // Find services to create (newly enabled)
        currentEnabledServices.forEach(currentService => {
            const numericCode = currentService.code.replace('ups_', '');
            const wasFetched = safeFetchedServices.find(fs => fs.service_code === numericCode);

            if (!wasFetched) {
                // This service was not previously saved, needs to be created
                servicesToCreate.push({
                    shipping_integration_id: upsConfigId || id || "",
                    service_code: numericCode,
                    service_name: currentService.name,
                    enabled: currentService.enabled,
                    adjustment_type: currentService.unit,
                    adjustment_value: parseFloat(currentService.amount) || 0
                });
            }
        });

        // Find services to update (enabled in both, but values changed)
        currentEnabledServices.forEach(currentService => {
            const numericCode = currentService.code.replace('ups_', '');
            const fetchedService = safeFetchedServices.find(fs => fs.service_code === numericCode);

            if (fetchedService) {
                const currentAmount = parseFloat(currentService.amount) || 0;
                const fetchedAmount = fetchedService.adjustment_value;
                const currentType = currentService.unit;
                const fetchedType = fetchedService.adjustment_type;

                // Check if values have changed
                if (currentAmount !== fetchedAmount || currentType !== fetchedType) {
                    servicesToUpdate.push({
                        _id: fetchedService._id,
                        enabled: currentService.enabled,
                        adjustment_type: currentService.unit,
                        adjustment_value: currentAmount
                    });
                }
            }
        });

        // Find services to delete (was enabled in fetched, now disabled)
        safeFetchedServices.forEach(fetchedService => {
            const numericCode = fetchedService.service_code;
            const currentService = services.find(s => s.code === `ups_${numericCode}`);

            if (!currentService || !currentService.enabled) {
                // This service was previously enabled but is now disabled
                servicesToDelete.push(fetchedService._id);
            }
        });

        return {
            toCreate: servicesToCreate,
            toUpdate: servicesToUpdate,
            toDelete: servicesToDelete
        };
    };

    /**
     * Comprehensive shipping services save function
     * Handles create, update, and delete operations based on user changes
     * Uses bulk operations for optimal performance
     */
    const saveShippingServices = async () => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found. Please save Account Information first.");
            return;
        }

        try {
            setLoading(prev => ({ ...prev, save: true }));
            setError("");

            // Validate adjustment values for enabled services
            const enabledServices = services.filter(service => service.enabled);
            const invalidServices = enabledServices.filter(service => {
                const amount = parseFloat(service.amount);
                return isNaN(amount) || amount < 0;
            });

            if (invalidServices.length > 0) {
                showError("Please enter valid adjustment values (numbers >= 0) for all enabled services.");
                return;
            }

            // Analyze what operations are needed
            const operations = analyzeServiceChanges();


            // Execute operations in sequence
            // Note: This is not a true transaction - if later operations fail, 
            // earlier operations won't be rolled back. This is acceptable for this use case
            // as users can easily re-save their changes.
            const results = {
                created: 0,
                updated: 0,
                deleted: 0
            };

            // 1. Delete services (first to clean up)
            if (operations.toDelete.length > 0) {
                await bulkDeleteShippingServices(operations.toDelete);
                results.deleted = operations.toDelete.length;
            }

            // 2. Update existing services
            if (operations.toUpdate.length > 0) {
                await bulkUpdateShippingServices(operations.toUpdate);
                results.updated = operations.toUpdate.length;
            }

            // 3. Create new services
            if (operations.toCreate.length > 0) {
                await bulkCreateShippingServices(operations.toCreate);
                results.created = operations.toCreate.length;
            }

            // Show success message with operation summary
            const totalOperations = results.created + results.updated + results.deleted;
            if (totalOperations > 0) {
                let message = "Shipping services saved successfully! ";
                const parts = [];
                if (results.created > 0) parts.push(`${results.created} created`);
                if (results.updated > 0) parts.push(`${results.updated} updated`);
                if (results.deleted > 0) parts.push(`${results.deleted} deleted`);
                message += `(${parts.join(', ')})`;
                showSuccess(message);
                // navigate(0);
            } else {
                showSuccess("No changes detected. Services are already up to date.");
                // navigate(0);
            }

            // Refresh the fetched services to reflect changes
            await fetchShippingServices(configId);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save shipping services";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, save: false }));
        }
    };

    // Fetch existing shipping services from API
    const fetchShippingServices = async (configId: string) => {
        try {
            setLoading(prev => ({ ...prev, fetch: true }));
            setError("");

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/services?shipping_integration_id=${configId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch shipping services");
            }

            const data: FetchedShippingService[] = await response.json();

            // Store fetched services for comparison - ensure it's always an array
            const servicesData = Array.isArray(data) ? data : [];
            setFetchedServices(servicesData);

            // Update services state with fetched data
            setServices(prevServices => {
                return prevServices.map(service => {
                    // Find matching service from API data
                    const fetchedService = servicesData?.find(fs =>
                        `ups_${fs.service_code}` === service.code
                    );

                    if (fetchedService) {
                        // Update with fetched data
                        return {
                            ...service,
                            enabled: fetchedService.enabled,
                            amount: fetchedService.adjustment_value.toString(),
                            unit: fetchedService.adjustment_type as (typeof PRICE_UNIT)[number],
                            sign: "+" // Default to positive adjustment
                        };
                    }

                    // Return original service if no match found
                    return service;
                });
            });

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to fetch shipping services";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    // Bulk create shipping services
    const bulkCreateShippingServices = async (createPayload: ServiceBulkPayload[]) => {
        if (createPayload.length === 0) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/services/bulk`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(createPayload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to create shipping services");
            }

            const data = await response.json();
            return data;

        } catch (err) {
            throw err;
        }
    };

    // Bulk update existing shipping services
    const bulkUpdateShippingServices = async (updatePayload: ServiceBulkUpdatePayload[]) => {
        if (updatePayload.length === 0) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/services/bulk`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updatePayload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to update shipping services");
            }

            const data = await response.json();
            return data;

        } catch (err) {
            throw err;
        }
    };

    // Bulk delete shipping services
    const bulkDeleteShippingServices = async (deletePayload: string[]) => {
        if (deletePayload.length === 0) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/services/bulk`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(deletePayload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete shipping services");
            }

            const data = await response.json();
            return data;

        } catch (err) {
            throw err;
        }
    };

    // ==================== SHIPMENT SETTINGS API FUNCTIONS ====================

    // Create UPS shipment settings
    const createShipmentSettings = async (payload: ShipmentSettingsPayload) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/shipment-settings`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to create shipment settings");
            }

            const data: ShipmentSettingsResponse = await response.json();
            setShipmentSettingsId(data._id);
            return data;

        } catch (err) {
            throw err;
        }
    };

    // Get UPS shipment settings (returns array, we use first item)
    const getShipmentSettings = async () => {
        try {
            setLoading(prev => ({ ...prev, fetch: true }));
            setError("");

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/shipment-settings?integration_id=${upsConfigId || id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to fetch shipment settings");
            }

            const data: ShipmentSettingsResponse[] = await response.json();

            // Use the first item from the array (index 0)
            if (data && data.length > 0) {
                const firstItem = data[0];
                setShipmentSettingsId(firstItem._id);
                setShipLabelFormat(firstItem.label_format);
                setShipLabelLayout(firstItem.label_layout);
            }

            return data;

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to fetch shipment settings";
            setError(errorMessage);
            showError(errorMessage);
            throw err;
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    // Update UPS shipment settings (PUT - full replace)
    const updateShipmentSettings = async (settingsId: string, payload: ShipmentSettingsPayload) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/shipment-settings/${settingsId}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to update shipment settings");
            }

            const data: ShipmentSettingsResponse = await response.json();
            return data;

        } catch (err) {
            throw err;
        }
    };

    // Save shipment settings (create or update)
    const saveShipmentSettings = async () => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found. Please save Account Information first.");
            return;
        }

        try {
            setLoading(prev => ({ ...prev, save: true }));
            setError("");

            const payload: ShipmentSettingsPayload = {
                shipping_integration_id: configId,
                label_format: shipLabelFormat,
                label_layout: shipLabelLayout,
            };

            if (shipmentSettingsId) {
                // Update existing settings
                await updateShipmentSettings(shipmentSettingsId, payload);
                showSuccess("Shipment settings updated successfully!");
                // navigate(0);
            } else {
                // Create new settings
                await createShipmentSettings(payload);
                showSuccess("Shipment settings created successfully!");
                // navigate(0);
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save shipment settings";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, save: false }));
        }
    };

    // Create UPS Shipper Address
    const createShipperAddress = async (payload: ShipperAddressPayload) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/shipper-addresses`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        showSuccess("Shipper address created successfully!");
        // navigate(0);
        return await response.json();
    };

    // Update UPS Shipper Address
    const updateShipperAddress = async (addressId: string, payload: ShipperAddressPayload) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/shipper-addresses/${addressId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        showSuccess("Shipper address updated successfully!");
        // navigate(0);
        return await response.json();
    };

    // Create UPS Package Options
    const createPackageOptions = async (payload: PackageOptionsPayload) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/package-options`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    };

    // Update UPS Package Options
    const updatePackageOptions = async (optionsId: string, payload: PackageOptionsPayload) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/package-options/${optionsId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    };

    // Create or update package options (use_multiple_packages) for current UPS config
    const upsertPackageOptions = async (configId: string) => {
        const packageOptionsPayload: PackageOptionsPayload = {
            shipping_integration_id: configId,
            use_multiple_packages: useMultiplePackages,
        };

        if (currentPackageOptionsId) {
            await updatePackageOptions(currentPackageOptionsId, packageOptionsPayload);
            return currentPackageOptionsId;
        }

        const createdOptions = await createPackageOptions(packageOptionsPayload);
        const newOptionsId = createdOptions?._id || createdOptions?.id;
        if (typeof newOptionsId === "string") {
            setCurrentPackageOptionsId(newOptionsId);
            return newOptionsId;
        }
        return undefined;
    };

    /** PATCH package-settings (separate from package-options) — sets use_multiple_packages on save. */
    // const patchPackageSettingsUseMultiple = async (settingsId: string) => {
    //     const response = await fetch(
    //         `${process.env.REACT_APP_API_URL}/shipping/ups/package-settings/${settingsId}`,
    //         {
    //             method: "PATCH",
    //             headers: {
    //                 "Content-Type": "application/json",
    //                 Authorization: `Bearer ${token}`,
    //             },
    //             body: JSON.stringify({ use_multiple_packages: true }),
    //         }
    //     );
    //     if (!response.ok) {
    //         const errorData = await response.json().catch(() => ({}));
    //         throw new Error(errorData.message || `Failed to update package settings (HTTP ${response.status})`);
    //     }
    //     return await response.json().catch(() => ({}));
    // };

    /** Normalize GET body: API may return one object or an array. */
    const normalizePackageOptionsList = (data: unknown): PackageOptionsResponse[] => {
        if (!data) return [];
        if (Array.isArray(data)) return data as PackageOptionsResponse[];
        if (typeof data === "object" && data !== null) {
            const o = data as Record<string, unknown>;
            if (o._id || o.id || o.shipping_integration_id) {
                return [data as PackageOptionsResponse];
            }
        }
        return [];
    };

    // Fetch UPS Package Options (GET by integration id; body includes options document `id` for PUT)
    const fetchPackageOptions = async (configId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/package-options/by-shipping-integration/${configId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 404) {
                setFetchedPackageOptions([]);
                setCurrentPackageOptionsId("");
                return [];
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const list = normalizePackageOptionsList(data);
            setFetchedPackageOptions(list);

            if (list.length > 0) {
                const opt = list[0];
                const optionsDocId = String(opt._id || opt.id || "");
                setCurrentPackageOptionsId(optionsDocId);
                setUseMultiplePackages(Boolean(opt.use_multiple_packages));
            } else {
                setCurrentPackageOptionsId("");
            }

            return list;
        } catch (error) {
            showError("Failed to fetch package options");
            setFetchedPackageOptions([]);
            setCurrentPackageOptionsId("");
            return [];
        }
    };

    // Fetch UPS Shipper Addresses
    const fetchShipperAddresses = async (configId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/shipper-addresses?integration_id=${configId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setFetchedShipperAddresses(data);

            // If we have addresses, populate the form with the first one
            if (data && data.length > 0) {
                const firstAddress = data[0];
                // Store the ID for update operations
                setCurrentShipperAddressId(firstAddress._id);

                setShipper({
                    name: firstAddress.shipper_name || "",
                    company: firstAddress.company_name || "",
                    phone: firstAddress.phone_number || "",
                    email: firstAddress.email_address || "",
                    addr1: firstAddress.address_line1 || "",
                    addr2: firstAddress.address_line2 || "",
                    city: firstAddress.city || "",
                    state: firstAddress.state || "",
                    postal: firstAddress.postal_code || "",
                    country: firstAddress.country || "",
                    labelFormat: firstAddress.label_format || "",
                    labelLayout: firstAddress.label_layout || "",
                    cityCode: firstAddress.city_code || "",
                    stateCode: firstAddress.state_code || "",
                    countryCode: firstAddress.country_code || "",
                    latitude: 0,
                    longitude: 0,
                });
            }

            return data;
        } catch (error) {
            showError("Failed to fetch shipper addresses");
            throw error;
        }
    };

    // Save shipper address
    const saveShipperAddress = async () => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found. Please save Account Information first.");
            return;
        }

        // Validate required fields
        if (!shipper.name.trim()) {
            showError("Shipper name is required");
            return;
        }
        if (!shipper.addr1.trim()) {
            showError("Address Line 1 is required");
            return;
        }
        if (!shipper.city.trim()) {
            showError("City is required");
            return;
        }
        if (!shipper.state.trim()) {
            showError("State is required");
            return;
        }
        if (!shipper.postal.trim()) {
            showError("Postal code is required");
            return;
        }
        if (!shipper.country.trim()) {
            showError("Country is required");
            return;
        }

        try {
            setLoading(prev => ({ ...prev, save: true }));
            setError("");

            const payload: ShipperAddressPayload = {
                shipping_integration_id: configId,
                shipper_name: shipper.name,
                company_name: shipper.company,
                phone_number: shipper.phone,
                email_address: shipper.email,
                address_line1: shipper.addr1,
                address_line2: shipper.addr2,
                city: shipper.city,
                city_code: shipper.cityCode,
                state: shipper.state,
                state_code: shipper.stateCode,
                country: shipper.country,
                country_code: shipper.countryCode,
                postal_code: shipper.postal,
                label_format: shipper.labelFormat,
                label_layout: shipper.labelLayout,
            };

            if (currentShipperAddressId) {
                // Update existing shipper address
                await updateShipperAddress(currentShipperAddressId, payload);
            } else {
                // Create new shipper address
                await createShipperAddress(payload);
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save shipper address";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, save: false }));
        }
    };

    /* ---------------------------- API Functions ----------------------------- */
    const saveAccountInformation = async () => {
        try {
            setLoading(prev => ({ ...prev, save: true }));
            setError("");

            const groupId = isTaxable
                ? (taxState.tax_group?._id ?? taxState.tax_group?.id ?? null)
                : null;

            const ruleId = isTaxable && !groupId
                ? (taxState.tax_rule?._id ?? taxState.tax_rule?.id ?? null)
                : null;

            const payload = {
                integration_type: "shipping",
                provider: "ups",
                auth_mode: "oauth_client_secret",
                client_id: clientId,
                client_secret: clientSecret,
                account_number: accountNumber,
                environment: environment,
                status: upsStatus ?? false,
                // NEW
                taxable: isTaxable,
                ...(isTaxable && groupId ? { tax_group_id: groupId } : {}),
                ...(isTaxable && !groupId && ruleId ? { tax_rule_id: ruleId } : {}),
            };


            const url = isEdit
                ? `${process.env.REACT_APP_API_URL}/shipping/ups/configs/${id}`
                : `${process.env.REACT_APP_API_URL}/shipping/ups/configs`;

            const response = await fetch(url, {
                method: isEdit ? "PATCH" : "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} UPS configuration`);
            }

            const data = await response.json();
            console.log(data,"data")
            // Extract and store the UPS config ID from the response
            let savedConfigId = "";
            if (data._id || data.id) {
                const configId = data._id || data.id;
                setUpsConfigId(configId);
                savedConfigId = configId;
            }

            showSuccess(`UPS configuration ${isEdit ? 'updated' : 'created'} successfully!`);
            
            // Automatically connect after successful save (only if not already connected)
            if (savedConfigId && !upsStatus) {
                // Call toggleUpsConnection with the saved config ID
                await toggleUpsConnectionWithId(savedConfigId);
            }

            // First-time create: redirect to edit route with returned config id
            if (!isEdit && savedConfigId) {
                navigate(`/signage/ups/settings/${savedConfigId}`);
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save UPS configuration";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, save: false }));
        }
    };



    const fetchUpsConfiguration = async (configId: string) => {
        try {
            setLoading(prev => ({ ...prev, fetch: true }));
            setError("");

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/configs/${configId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch UPS configuration");
            }

            const data = await response.json();
               console.log(data,"dataget config")
            // Store the UPS config ID
            if (data._id || data.id) {
                const configId = data._id || data.id;
                setUpsConfigId(configId);
            }

            // Populate form with existing data
            setAuthMode("OAuth (Client Secret)"); // Since we only support OAuth now
            setClientId(data.client_id || "");
            setClientSecret(data.client_secret || "");
            setAccountNumber(data.account_number || "");
            setEnvironment(data.environment || "sandbox");
            setUpsStatus(data.status || false);

            // ⬇️ ADD THIS RIGHT HERE (after setUpsStatus)
            const oid = (v: any) => (v && typeof v === "object" && "$oid" in v ? v.$oid : v);

            const taxable = !!data?.taxable;
            setIsTaxable(taxable);

            const groupId = oid(data?.tax_group_id) || oid(data?.tax_group?._id) || oid(data?.tax_group?.id);
            const ruleId = oid(data?.tax_rule_id) || oid(data?.tax_rule?._id) || oid(data?.tax_rule?.id);

            const encoded = !taxable ? "" : (groupId ? `group-${groupId}` : (ruleId ? `rule-${ruleId}` : ""));

            // this screen uses the small "formik-like" adapter (taxFormik) backed by taxState
            setTaxState((s) => ({
                ...s,
                tax_rule_id: encoded,
                tax_group: groupId ? { ...(data?.tax_group || {}), _id: groupId } : null,
                tax_rule: ruleId ? { ...(data?.tax_rule || {}), _id: ruleId } : null,
            }));


        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to fetch UPS configuration";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    // Fetch configuration data when in edit mode
    useEffect(() => {
        if (isEdit && id) {
            fetchUpsConfiguration(id);
        }
    }, [isEdit, id]);

    // Fetch packages when we have a configId
    useEffect(() => {
        const configId = upsConfigId || id;
        if (configId) {
            fetchAllPackages(configId);
            fetchShippingServices(configId);
            getShipmentSettings();
            fetchShipperAddresses(configId);
            fetchPackageOptions(configId);
        }
    }, [upsConfigId, id]);

    // Toggle UPS connection status with configId parameter
    const toggleUpsConnectionWithId = async (configId: string) => {
        if (!configId) {
            showError("No UPS configuration ID found");
            return;
        }

        try {
            setLoading(prev => ({ ...prev, connect: true }));
            setError("");

            const newStatus = !upsStatus;

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/configs/${configId}`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        status: newStatus
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to ${newStatus ? 'connect' : 'disconnect'} UPS account`);
            }

            setUpsStatus(newStatus);
            showSuccess(`UPS account ${newStatus ? 'connected' : 'disconnected'} successfully!`);
            // navigate(0);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to toggle UPS connection";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, connect: false }));
        }
    };

    // Toggle UPS connection status (wrapper for existing ID)
    const toggleUpsConnection = async () => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found");
            return;
        }
        await toggleUpsConnectionWithId(configId);
    };

    // Fetch all packages for UPS configuration
    const fetchAllPackages = async (configId: string) => {
        try {
            setLoading(prev => ({ ...prev, fetch: true }));
            setError("");

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/packages?integration_id=${configId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch packages");
            }

            const data = await response.json();
            // Convert API response to PackageCard format and add to packageCards
            if (data && Array.isArray(data)) {
                const fetchedPackages: PackageCard[] = data.map((pkg: any, index: number) => ({
                    id: pkg._id || pkg.id || `fetched-${index}-${Date.now()}`,
                    presetValue: pkg.package_type === "CUSTOM" ? "custom_pkg" : "ups_preset",
                    displayName: pkg.package_type === "CUSTOM" ? pkg.name : pkg.name,
                    packageName: pkg.name,
                    code: pkg.ups_code || "",
                    name: pkg.name,
                    width: pkg.width || "",
                    height: pkg.height || "",
                    length: pkg.length || "",
                    maxWeight: pkg.weight || "",
                    additionalWeight: pkg.additional_weight ?? 0,
                }));

                // Add fetched packages to existing packageCards (avoid duplicates)
                setPackageCards(prevCards => {
                    const existingIds = new Set(prevCards.map(card => card.id));
                    const newPackages = fetchedPackages.filter(pkg => !existingIds.has(pkg.id));
                    return [...prevCards, ...newPackages];
                });
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to fetch packages";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    /* ---------------------------- Package Settings API Functions ----------------------------- */

    // Create individual package — returns new package document id, or null on failure
    const createIndividualPackage = async (pkg: PackageCard): Promise<string | null> => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found. Please save Account Information first.");
            return null;
        }

        try {
            const payload = {
                shipping_integration_id: configId,
                name: pkg.presetValue === "custom_pkg" ? pkg.packageName : pkg.name,
                package_type: pkg.presetValue === "custom_pkg" ? "CUSTOM" : "UPS_DEFAULT",
                ups_code: pkg.code,
                length: Number(pkg.length) || 0,
                width: Number(pkg.width) || 0,
                height: Number(pkg.height) || 0,
                weight: Number(pkg.maxWeight) || 0,
                unit: "IN_LBS",
                status: true,
                additional_weight: Number(pkg.additionalWeight) || 0,
            };


            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/shipping/ups/packages`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to create package");
            }

            const data = await response.json();
            const newId = data?._id || data?.id;
            return typeof newId === "string" ? newId : null;

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to create package";
            showError(errorMessage);
            return null;
        }
    };

    // Save package settings - first create/update packages, then save package options
    const savePackageSettings = async () => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found. Please save Account Information first.");
            return;
        }

        // Filter only newly added packages (local packages with UPS codes as IDs)
        const newlyAddedPackages = packageCards.filter(pkg => {
            // Newly added packages have UPS codes as IDs (like "01", "21", "2a", etc.)
            // Fetched packages have MongoDB ObjectIds (24 chars) or start with 'fetched-'
            return !pkg.id.startsWith('fetched-') && pkg.id.length !== 24;
        });

        try {
            setLoading(prev => ({ ...prev, save: true }));
            setError("");

            let mutable = packageCards.map((c) => ({ ...c }));

            // 1) Create / Update all custom packages first
            const customIndices = mutable
                .map((c, i) => (c.presetValue === "custom_pkg" ? i : -1))
                .filter((i) => i >= 0);

            for (const i of customIndices) {
                const cur = mutable[i];
                if (!cur.packageName || !cur.width || !cur.height || !cur.length || !cur.maxWeight) {
                    showError("Please fill in all required fields for every custom package.");
                    return;
                }
            }

            for (const i of customIndices) {
                const cur = mutable[i];
                if (!isPersistedPackageId(cur.id)) {
                    const newId = await createIndividualPackage(cur);
                    if (!newId) {
                        showError(`Failed to create package: ${cur.displayName}`);
                        return;
                    }
                    mutable = mutable.map((c, idx) => (idx === i ? { ...c, id: newId } : c));
                } else {
                    const payload = {
                        shipping_integration_id: configId,
                        name: cur.packageName,
                        package_type: "CUSTOM",
                        ups_code: cur.code,
                        length: Number(cur.length) || 0,
                        width: Number(cur.width) || 0,
                        height: Number(cur.height) || 0,
                        weight: Number(cur.maxWeight) || 0,
                        unit: "IN_LBS",
                        status: true,
                        additional_weight: Number(cur.additionalWeight) || 0,
                    };

                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/shipping/ups/packages/${cur.id}`,
                        {
                            method: "PUT",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(payload),
                        }
                    );

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.message || "Failed to update package");
                    }
                    await response.json();
                }
            }

            // 2) Create newly added UPS preset packages; update persisted presets (additional weight)
            const newlyAddedPresetPackages = newlyAddedPackages.filter(
                (pkg) => pkg.presetValue !== "custom_pkg"
            );

            const persistedPresetIndices = mutable
                .map((c, i) => (c.presetValue !== "custom_pkg" && isPersistedPackageId(c.id) ? i : -1))
                .filter((i) => i >= 0);

            for (const i of persistedPresetIndices) {
                const cur = mutable[i];
                const payload = {
                    shipping_integration_id: configId,
                    name: cur.name,
                    package_type: "UPS_DEFAULT",
                    ups_code: cur.code,
                    length: Number(cur.length) || 0,
                    width: Number(cur.width) || 0,
                    height: Number(cur.height) || 0,
                    weight: Number(cur.maxWeight) || 0,
                    unit: "IN_LBS",
                    status: true,
                    additional_weight: Number(cur.additionalWeight) || 0,
                };

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/shipping/ups/packages/${cur.id}`,
                    {
                        method: "PUT",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || "Failed to update package");
                }
                await response.json();
            }

            for (let i = 0; i < newlyAddedPresetPackages.length; i++) {
                const pkg = newlyAddedPresetPackages[i];
                const newPkgId = await createIndividualPackage(pkg);
                if (!newPkgId) {
                    showError(`Failed to create package: ${pkg.displayName}`);
                    return;
                }
                mutable = mutable.map((c) => (c.id === pkg.id ? { ...c, id: newPkgId } : c));
            }

            setPackageCards(mutable);

            // 3) Save package options LAST (use_multiple_packages)
            await upsertPackageOptions(configId);

            const changedCount =
                customIndices.length + persistedPresetIndices.length + newlyAddedPresetPackages.length;
            if (changedCount > 0) {
                showSuccess(`Packages saved successfully! ${changedCount} package(s) processed.`);
            } else {
                showSuccess("Package settings saved successfully!");
            }
            // navigate(0);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save package settings";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(prev => ({ ...prev, save: false }));
        }
    };

    const isPersistedPackageId = (pid: string) =>
        /^[a-f0-9]{24}$/i.test(pid) && !pid.startsWith("fetched-");

    /** One action: validate and create/update every custom ("My Own Package") card in order. */
    const updateAllCustomPackages = async () => {
        const configId = upsConfigId || id;
        if (!configId) {
            showError("No UPS configuration ID found. Please save Account Information first.");
            return;
        }

        let mutable = packageCards.map((c) => ({ ...c }));
        const customIndices = mutable
            .map((c, i) => (c.presetValue === "custom_pkg" ? i : -1))
            .filter((i) => i >= 0);

        if (customIndices.length === 0) {
            return;
        }

        for (const i of customIndices) {
            const cur = mutable[i];
            if (!cur.packageName || !cur.width || !cur.height || !cur.length || !cur.maxWeight) {
                showError("Please fill in all required fields for every custom package.");
                return;
            }
        }

        try {
            setLoading((prev) => ({ ...prev, save: true }));
            setError("");

            // Keep package options in sync when user clicks "Update Package" in custom package flow
            await upsertPackageOptions(configId);

            for (const i of customIndices) {
                const cur = mutable[i];

                if (!isPersistedPackageId(cur.id)) {
                    const newId = await createIndividualPackage(cur);
                    if (!newId) {
                        return;
                    }
                    mutable = mutable.map((c, idx) => (idx === i ? { ...c, id: newId } : c));
                } else {
                    const payload = {
                        shipping_integration_id: configId,
                        name: cur.packageName,
                        package_type: "CUSTOM",
                        ups_code: cur.code,
                        length: Number(cur.length) || 0,
                        width: Number(cur.width) || 0,
                        height: Number(cur.height) || 0,
                        weight: Number(cur.maxWeight) || 0,
                        unit: "IN_LBS",
                        status: true,
                    };

                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/shipping/ups/packages/${cur.id}`,
                        {
                            method: "PUT",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(payload),
                        }
                    );

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.message || "Failed to update package");
                    }

                    await response.json();
                    mutable = mutable.map((c, idx) =>
                        idx === i ? { ...c, ...payload } : c
                    );
                }
            }

            setPackageCards(mutable);
            showSuccess(
                customIndices.length === 1
                    ? "Custom package updated successfully!"
                    : `${customIndices.length} custom packages updated successfully!`
            );
            // navigate(0);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to update packages";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading((prev) => ({ ...prev, save: false }));
        }
    };

    /* ---------------------------- Utilities ----------------------------- */
    const hasMissingRequiredAccountFields =
        !String(authMode || "").trim() ||
        !String(clientId || "").trim() ||
        !String(clientSecret || "").trim() ||
        !String(accountNumber || "").trim() ||
        !String(environment || "").trim();
    const accountInvalid = accountTried && hasMissingRequiredAccountFields;
    const canConnect =
        !!String(authMode || "").trim() &&
        !!String(clientId || "").trim() &&
        !!String(clientSecret || "").trim() &&
        !!String(accountNumber || "").trim() &&
        !!String(environment || "").trim();

    /* ------------------------- TAB: Account Info ------------------------ */
    const AccountInformation = (
        <>
            <div className="row g-3 border-bottom pb-3">
                <div className="col-12">
                    <div className="col-12 col-lg-6">
                        <label className="form-label">
                            Authentication Mode <span className="text-danger">*</span>
                        </label>
                        <Select
                            classNamePrefix="rs"
                            placeholder="Select authentication mode"
                            options={AUTH_MODES.map((m) => ({ value: m, label: m }))}
                            value={{ value: authMode, label: authMode }}
                            onChange={(o: any) => setAuthMode(o?.value || "")}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                        />
                        {accountTried && !String(authMode || "").trim() && (
                            <div className="text-danger small mt-1">Authentication Mode is required.</div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="col-12 col-lg-7">
                        <label className="form-label">
                            Client ID <span className="text-danger">*</span>
                        </label>
                        <input
                            className={`ups-form-control ${accountInvalid && !clientId ? "is-invalid" : ""}`}
                            placeholder="Enter client ID"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            name="ups_client_id"
                            autoComplete="off"
                            data-lpignore="true"
                        />
                        {accountTried && !String(clientId || "").trim() && (
                            <div className="text-danger small mt-1">Client ID is required.</div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="col-12 col-lg-7">
                        <label className="form-label">
                            Client Secret <span className="text-danger">*</span>
                        </label>
                        <input
                            type="password"
                            className={`ups-form-control ${accountInvalid && !clientSecret ? "is-invalid" : ""}`}
                            placeholder="Enter client secret"
                            value={clientSecret}
                            onChange={(e) => setClientSecret(e.target.value)}
                            name="ups_client_secret"
                            autoComplete="new-password"
                            data-lpignore="true"
                        />
                        {accountTried && !String(clientSecret || "").trim() && (
                            <div className="text-danger small mt-1">Client Secret is required.</div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="col-12 col-lg-7">
                        <label className="form-label">
                            Account Number <span className="text-danger">*</span>
                        </label>
                        <input
                            className={`ups-form-control ${accountInvalid && !accountNumber ? "is-invalid" : ""}`}
                            placeholder="Enter account number"
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value)}
                        />
                        {accountTried && !String(accountNumber || "").trim() && (
                            <div className="text-danger small mt-1">Account Number is required.</div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="col-12 col-lg-6">
                        <label className="form-label">
                            Environment <span className="text-danger">*</span>
                        </label>
                        <Select
                            classNamePrefix="rs"
                            placeholder="Select environment"
                            options={ENVIRONMENT_OPTIONS}
                            value={ENVIRONMENT_OPTIONS.find(env => env.value === environment)}
                            onChange={(o: any) => setEnvironment(o?.value || "sandbox")}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                        />
                        {accountTried && !String(environment || "").trim() && (
                            <div className="text-danger small mt-1">Environment is required.</div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="col-12 col-lg-7">
                        <div className="d-flex align-items-center" style={{ gap: 28 }}>
                            {/* Is Taxable checkbox (parent) */}
                            <div className="form-check d-flex align-items-center gap-2">
                                <input
                                    id="ups_is_taxable"
                                    type="checkbox"
                                    className="form-check-input square-checkbox"
                                    checked={isTaxable}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setIsTaxable(checked);
                                        if (!checked) {
                                            // clear dropdown + objects when turning OFF
                                            setTaxState({ tax_rule_id: "", tax_group: null, tax_rule: null });
                                        }
                                    }}
                                />
                                <label htmlFor="ups_is_taxable" className="form-check-label">Is Taxable</label>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">

                    <TaxRuleSelect
                      key={`taxsel-${isTaxable}-${taxState.tax_rule_id}`} 
                        token={token!}
                        taxable={isTaxable}
                        formik={taxFormik}
                        taxRuleIdName="tax_rule_id"
                        groupFieldName="tax_group"
                        ruleFieldName="tax_rule"
                        wrapperClassName="d-flex flex-column"   // keeps it inline
                        selectClassName="form-select"
                        label="Tax Rule"
                    />
                </div>


                <div className="col-12 d-flex justify-content-end mb-1">
                    <button
                        type="button"
                        className="btn success-btn btn-success fw-bold fs-14 surface-btn"
                        disabled={loading.save}
                        onClick={() => {
                            setAccountTried(true);
                            if (!hasMissingRequiredAccountFields) {
                                saveAccountInformation();
                            }
                        }}
                    >
                        {loading.save ? "Saving..." : id ? "Update Account Information" : "Save Account & Connect"}
                    </button>
                </div>
            </div>



            <div className="d-flex flex-column-reverse flex-md-row gap-3 gap-md-0 justify-content-between mt-4">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => window.history.back()}>
                    <ChevronLeft size={16} className="me-1" />
                    Back
                </button>

                {/* Connect/Disconnect UPS Account Button - Only show if we have a saved configuration */}
                {(upsConfigId || id) && (
                    <button
                        type="button"
                        className={`btn fw-bold fs-16 px-4 ${upsStatus
                            ? "btn-danger"
                            : "btn-primary save-btn surface-btn"
                            }`}
                        disabled={loading.connect}
                        onClick={toggleUpsConnection}
                    >
                        {loading.connect ? "Processing..." : (
                            upsStatus ? "Disconnect" : "Connect"
                        )}
                    </button>
                )}
            </div>
        </>
    );

    /* ------------------------- TAB: Package Settings -------------------- */
    const PackageSettings = (
        <>
            <div className="border-bottom pb-3">
                <div className="row g-3">
                    <div className="col-12">
                        <div className="col-12 col-lg-6">
                            <label className="form-label">
                                Use multiple-package shipments <span className="text-danger">*</span>
                            </label>
                            <Select
                                classNamePrefix="rs"
                                placeholder="Select option"
                                options={YES_NO.map((v) => ({ value: v, label: v }))}
                                value={{ value: useMultiplePackages ? "Yes" : "No", label: useMultiplePackages ? "Yes" : "No" }}
                                onChange={(o: any) => setUseMultiplePackages(o?.value === "Yes")}
                                components={{ DropdownIndicator }}
                                styles={customSelectStyles}
                            />
                            <div className="fs-14 mt-2">If "NO", each package will be considered a separate shipment (shipments will be more expensive).</div>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="col-12 col-lg-8">
                            <label className="form-label">Select Package Type</label>
                            <div className="row g-2 align-items-end">
                                <div className="col-6">
                                    <Select
                                        classNamePrefix="rs"
                                        placeholder="Select package type to add"
                                        options={PACKAGE_PRESET_OPTIONS}
                                        value={newPkgType ? allPresetsFlat.find((o) => o.value === newPkgType) ?? null : null}
                                        onChange={(o: any) => {
                                            const selectedValue = o?.value || "";
                                            setNewPkgType(selectedValue);
                                            if (!selectedValue) return;
                                            // With "No" for multi-package + My Own Package, cards are added only via the Own Package button.
                                            if (!useMultiplePackages && selectedValue === "custom_pkg") {
                                                return;
                                            }
                                            handleAddPackageCard(selectedValue);
                                        }}
                                        components={{ DropdownIndicator, Option: PackageOption }}
                                        styles={customSelectStyles}
                                    />
                                </div>
                                <div className="col-4 d-flex align-items-center">
                                    { newPkgType === "custom_pkg" && (
                                        <button
                                            type="button"
                                            className="btn btn-primary  table-addbutton fw-bold fs-14 w-50"
                                            disabled={loading.save}
                                            onClick={addOwnPackageCard}
                                        >
                                            Add
                                        </button>
                                    )}
                                </div>
                            </div>
                            {newPkgType === "custom_pkg" && (
                                <div className="fs-14 mt-2 text-muted">
                                    Each click adds another custom package row.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Fetched Packages Section */}
                    {packageCards.length > 0 && (
                        <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="mb-0 fw-semibold">Existing Packages ({packageCards.length})</h6>
                                <small className="text-muted">Packages loaded from UPS configuration</small>
                            </div>
                        </div>
                    )}

                    {/* Cards */}
                    <div className="col-12">
                        <div className="d-flex flex-column gap-3">
                            {packageCards.map((pkg, idx) => (
                                <div key={pkg.id} className="pkg-card p-3 rounded-4 border">
                                    <div className="align-items-start border-bottom d-flex justify-content-between mb-4">
                                        <div>
                                            <div className="fw-semibold fs-20">Package {idx + 1}</div>
                                            <p className="fs-14 fw-medium mt-2" style={{ color: "#155dfc" }}>
                                                {pkg.presetValue === "custom_pkg" ? pkg.packageName : pkg.displayName}
                                            </p>
                                        </div>
                                        <button
                                            className="btn btn-link text-muted p-1 delete-btn"
                                            title="Remove package"
                                            disabled={loading.save}
                                            onClick={() => removePackageCard(pkg.id)}
                                        >
                                            {loading.save ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Deleting...</span>
                                                </div>
                                            ) : (
                                                <Trash2 size={18} />
                                            )}
                                        </button>
                                    </div>

                                    {/* Package Name Field - Only show for custom packages */}
                                    {pkg.presetValue === "custom_pkg" && (
                                        <div className="row g-3 mb-3">
                                            <div className="col-12 col-lg-6">
                                                <label className="form-label">
                                                    Package Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="ups-form-control"
                                                    placeholder="Enter a name for your custom package"
                                                    value={pkg.packageName}
                                                    onChange={(e) => updateCard(pkg.id, "packageName", e.target.value)}
                                                />
                                                <div className="fs-14 mt-2 text-muted">
                                                    Give this package type a descriptive name (e.g., "Small Box", "Mailer Envelope")
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row g-3">
                                          <div className="col-12 col-lg-4">
                                            <label className="form-label">Length<span className="text-danger">*</span> (in)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="ups-form-control"
                                                value={pkg.length}
                                                onChange={(e) => updateCard(pkg.id, "length", e.target.value)}
                                                disabled={pkg.presetValue !== "custom_pkg"}
                                            />
                                        </div>
                                        <div className="col-12 col-lg-4">
                                            <label className="form-label">Width<span className="text-danger">*</span> (in)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="ups-form-control"
                                                value={pkg.width}
                                                onChange={(e) => updateCard(pkg.id, "width", e.target.value)}
                                                disabled={pkg.presetValue !== "custom_pkg"}
                                            />
                                        </div>
                                        <div className="col-12 col-lg-4">
                                            <label className="form-label">Height<span className="text-danger">*</span> (in)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="ups-form-control"
                                                value={pkg.height}
                                                onChange={(e) => updateCard(pkg.id, "height", e.target.value)}
                                                disabled={pkg.presetValue !== "custom_pkg"}
                                            />
                                        </div>
                                        <div className="col-12 col-lg-4">
                                            <label className="form-label">Maximum Weight<span className="text-danger">*</span> (lbs)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="ups-form-control"
                                                value={pkg.maxWeight}
                                                onChange={(e) => updateCard(pkg.id, "maxWeight", e.target.value)}
                                                disabled={pkg.presetValue !== "custom_pkg"}
                                            />
                                        </div>
                                        <div className="col-12 col-lg-4">
                                            <label className="form-label">Additional Weight (lbs)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="ups-form-control"
                                                value={pkg.additionalWeight}
                                                onChange={(e) => updateCard(pkg.id, "additionalWeight", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {packageCards.length === 0 && (
                                <div className="text-muted small">No packages added yet.</div>
                            )}
                        </div>
                    </div>

                    <div className="col-12 d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn success-btn fw-bold fs-14 surface-btn btn-success"
                            disabled={loading.save}
                            onClick={savePackageSettings}
                        >
                            {loading.save ? "Creating Packages..." : "Save Package Settings"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="d-flex flex-column-reverse flex-md-row gap-3 gap-md-0 justify-content-between mt-4">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => window.history.back()}>
                    <ChevronLeft size={16} className="me-1" />
                    Back
                </button>
            </div>
        </>
    );


    /* ------------------------- TAB: Shipping Carrier -------------------- */
    const ShippingCarrier = (
        <>
            <div className="pb-3">
                <h5 className="mb-2">UPS Shipping Services</h5>
                <div className="mb-3">
                    Configure which UPS services to enable and their pricing adjustments
                </div>

                <div className="carrier-card">
                    <div className="table-responsive">
                        <table className="table align-middle mb-0">
                            <thead className="carrier-thead">
                                <tr>
                                    <th style={{ width: 140 }}>SERVICE CODE</th>
                                    <th>NAME</th>
                                    <th style={{ width: 120 }}>ENABLED</th>
                                    <th style={{ width: 420 }}>PRICE ADJUSTMENT</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading.fetch ? (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4">
                                            <div className="d-flex align-items-center justify-content-center">
                                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                Loading shipping services...
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {services.map((s) => (
                                            <tr key={s.code}>
                                                <td className="svc-code">{s.code}</td>
                                                <td>{s.name}</td>
                                                <td>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`svc_${s.code}`}
                                                            checked={s.enabled}
                                                            onChange={(e) =>
                                                                updateService(s.code, "enabled", e.target.checked)
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                                        {/* amount */}
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="ups-form-control carrier-amount"
                                                            placeholder={s.unit === "%" ? "Percentage (%)" : "Amount ($)"}
                                                            value={s.amount}
                                                            onChange={(e) =>
                                                                updateService(s.code, "amount", e.target.value)
                                                            }
                                                            aria-label="Adjustment amount"
                                                            onWheel={(e) => e.currentTarget.blur()}
                                                        />

                                                        {/* unit */}
                                                        <div className="carrier-unit-select">
                                                            <Select
                                                                classNamePrefix="rs"
                                                                options={PRICE_UNIT.map((u) => ({
                                                                    value: u,
                                                                    label: u,
                                                                }))}
                                                                value={{ value: s.unit, label: s.unit }}
                                                                onChange={(o: any) =>
                                                                    updateService(s.code, "unit", o?.value || "%")
                                                                }
                                                                components={{ DropdownIndicator }}
                                                                styles={{
                                                                    ...customSelectStyles,
                                                                    control: (provided: any) => ({
                                                                        ...provided,
                                                                        minHeight: "39px",
                                                                        height: "39px",
                                                                        fontSize: "14px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                    }),
                                                                    valueContainer: (provided: any) => ({
                                                                        ...provided,
                                                                        padding: "2px 8px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                    }),
                                                                    indicatorsContainer: (provided: any) => ({
                                                                        ...provided,
                                                                        padding: "0 4px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        height: "100%",
                                                                    }),
                                                                    dropdownIndicator: (provided: any) => ({
                                                                        ...provided,
                                                                        padding: 0,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                    }),
                                                                }}
                                                                isSearchable={false}
                                                            />
                                                        </div>
                                                    </div>

                                                    <small className="text-muted d-block mt-1">
                                                        {s.unit === "%" ? "Percentage" : "Fixed amount"}
                                                    </small>
                                                </td>
                                            </tr>
                                        ))}

                                        {services.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted py-5">
                                                    No services to configure.
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="d-flex justify-content-end mt-3">
                    <button
                        className="btn success-btn btn-success fw-bold fs-14 surface-btn"
                        disabled={loading.save}
                        onClick={saveShippingServices}
                    >
                        {loading.save ? "Saving Services..." : "Save Carrier Settings"}
                    </button>
                </div>
            </div>

            <div className="d-flex flex-column-reverse flex-md-row gap-3 gap-md-0 justify-content-between mt-4">
                <button
                    type="button"
                    className="btn back-btn surface-btn fw-bold fs-16"
                    onClick={() => window.history.back()}
                >
                    <ChevronLeft size={16} className="me-1" />
                    Back
                </button>
            </div>
        </>
    );

    /* ------------------------- TAB: Shipment Settings ------------------- */
    const ShipmentSettings = (
        <>
            <div className="row">
                <h5 className="mb-2">Shipment Label Configuration</h5>
                <div className="mb-3">
                    Configure shipping label format and layout settings for your UPS shipments
                </div>
            </div>
            <div className="row g-3 border-bottom pb-3">
                <div className="col-12">
                    <div className="col-12 col-lg-6">
                        <label className="form-label">
                            Shipping Label Format <span className="text-danger">*</span>
                        </label>
                        <Select
                            classNamePrefix="rs"
                            placeholder="Select label format"
                            options={LABEL_FORMATS}
                            value={LABEL_FORMATS.find(f => f.value === shipLabelFormat) || null}
                            onChange={(o: any) => setShipLabelFormat(o?.value || "")}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                        />
                    </div>
                </div>

                <div className="col-12">
                    <div className="col-12 col-lg-6">
                        <label className="form-label">
                            Shipping Label Layout <span className="text-danger">*</span>
                        </label>
                        <Select
                            classNamePrefix="rs"
                            placeholder="Select label layout"
                            options={LABEL_LAYOUTS}
                            value={LABEL_LAYOUTS.find(l => l.value === shipLabelLayout) || null}
                            onChange={(o: any) => setShipLabelLayout(o?.value || "")}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                        />
                    </div>
                </div>

                <div className="col-12 d-flex justify-content-end mb-1">
                    <button
                        type="button"
                        className="btn success-btn btn-success fw-bold fs-14 surface-btn"
                        disabled={loading.save}
                        onClick={saveShipmentSettings}
                    >
                        {loading.save ? "Saving Settings..." : "Save Shipment Settings"}
                    </button>
                </div>
            </div>

            <div className="d-flex flex-column-reverse flex-md-row gap-3 gap-md-0 justify-content-between mt-4">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => window.history.back()}>
                    <ChevronLeft size={16} className="me-1" />
                    Back
                </button>
            </div>
        </>
    );

    /* ------------------------- TAB: Shipper Address --------------------- */
    const ShipperAddress = (
        <>
            <div className="row">
                <h5 className="mb-2">Package's origin address information</h5>
                <div className="mb-3">
                    Enter the shipper details and origin address for your packages
                </div>
            </div>
            <div className="row g-3 border-bottom pb-3">
                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        Shipper Name <span className="text-danger">*</span>
                    </label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter shipper name"
                        value={shipper.name}
                        onChange={(e) => setShipper({ ...shipper, name: e.target.value })}
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <label className="form-label">Company Name</label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter company name"
                        value={shipper.company}
                        onChange={(e) => setShipper({ ...shipper, company: e.target.value })}
                    />
                </div>

                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter phone number"
                        value={shipper.phone}
                        onChange={(e) => setShipper({ ...shipper, phone: e.target.value })}
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <label className="form-label">Email Address</label>
                    <input
                        type="email"
                        className="ups-form-control"
                        placeholder="Enter email address"
                        value={shipper.email}
                        onChange={(e) => setShipper({ ...shipper, email: e.target.value })}
                    />
                </div>

                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        Address Line 1 <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                        <i className="bi bi-geo-alt position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ pointerEvents: "none", zIndex: 1 }} />
                        <AddressAutocomplete
                            id="addr1"
                            name="addr1"
                            value={shipper.addr1}
                            onChange={handleShipperAddr1Change}
                            onSelect={(picked) => {
                                setShipper(prev => ({
                                    ...prev,
                                    addr1: picked.addressLine1,
                                    city: picked.city,
                                    cityCode: picked.cityCode,
                                    state: picked.state,
                                    stateCode: picked.stateCode,
                                    postal: picked.zipcode,
                                    country: picked.country,
                                    countryCode: picked.countryCode,
                                    latitude: picked.latitude,
                                    longitude: picked.longitude,
                                }));
                            }}
                            className="ups-form-control ps-5"
                            placeholder="Enter street address"
                        />
                    </div>
                </div>
                <div className="col-12 col-lg-6">
                    <label className="form-label">Address Line 2</label>
                    <input
                        className="ups-form-control"
                        placeholder="Apartment, suite, etc. (optional)"
                        value={shipper.addr2}
                        onChange={(e) => setShipper({ ...shipper, addr2: e.target.value })}
                    />
                </div>

                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        City <span className="text-danger">*</span>
                    </label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter city"
                        value={shipper.city}
                        onChange={(e) => setShipper({ ...shipper, city: e.target.value })}
                        disabled
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        State <span className="text-danger">*</span>
                    </label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter state"
                        value={shipper.state}
                        onChange={(e) => setShipper({ ...shipper, state: e.target.value })}
                        disabled
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        Postal Code <span className="text-danger">*</span>
                    </label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter postal code"
                        value={shipper.postal}
                        onChange={(e) => setShipper({ ...shipper, postal: e.target.value })}
                        disabled
                    />
                </div>

                <div className="col-12 col-lg-6">
                    <label className="form-label">
                        Country <span className="text-danger">*</span>
                    </label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter country"
                        value={shipper.country}
                        onChange={(e) => setShipper({ ...shipper, country: e.target.value })}
                        disabled
                    />
                </div>

                <div className="col-12 col-lg-6">
                    <label className="form-label">Shipping Label Format</label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter shipping label format"
                        value={shipper.labelFormat}
                        onChange={(e) => setShipper({ ...shipper, labelFormat: e.target.value })}
                    />
                </div>

                <div className="col-12 col-lg-6">
                    <label className="form-label">Shipping Label Layout</label>
                    <input
                        className="ups-form-control"
                        placeholder="Enter shipping label layout"
                        value={shipper.labelLayout}
                        onChange={(e) => setShipper({ ...shipper, labelLayout: e.target.value })}
                    />
                </div>

                <div className="col-12 d-flex justify-content-end mb-1">
                    <button
                        type="button"
                        className="btn success-btn btn-success fw-bold fs-14 surface-btn"
                        disabled={loading.save}
                        onClick={saveShipperAddress}
                    >
                        {loading.save ? "Saving Address..." : (currentShipperAddressId ? "Update Shipper Address" : "Save Shipper Address")}
                    </button>
                </div>
            </div>

            <div className="d-flex flex-column-reverse flex-md-row gap-3 gap-md-0 justify-content-between mt-4">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => window.history.back()}>
                    <ChevronLeft size={16} className="me-1" />
                    Back
                </button>
            </div>
        </>
    );

    /* ------------------------------ RENDER ------------------------------- */
    return (
        <>
            <div className="container" id="ups-connection-top">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10">
                        <div className="mb-4">
                            <h3 className="mb-1">
                                {isEdit ? "Edit UPS Configuration" : "Add UPS Configuration"}
                            </h3>
                        </div>

                        <div className="box-shadow-datatables card shadow-border-1 mb-3 py-4">
                            <div className="bg-white border-0 card-header pt-3 rounded-top-5 px-4">
                                <div className="d-flex align-items-center gap-2">
                                    <Truck size={22} style={{ color: "#e17100" }} />
                                    <h4 className="mb-1">UPS Account Information</h4>
                                </div>

                                {/* Tabs */}
                                <div className="rc-tabs mt-3">
                                    {(Object.keys(TAB_META) as TabKey[]).map((k) => (
                                        <TabButton
                                            key={k}
                                            active={active === k}
                                            color={TAB_META[k].color}
                                            label={TAB_META[k].label}
                                            shortLabel={TAB_META[k].shortLabel}
                                            icon={TAB_META[k].icon}
                                            onClick={() => setActive(k)}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="card-body pt-2 px-4">
                                {loading.fetch ? (
                                    <div className="d-flex justify-content-center align-items-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="ms-2">Loading UPS configuration...</span>
                                    </div>
                                ) : (
                                    <>
                                        {active === "account" && AccountInformation}
                                        {active === "packages" && PackageSettings}
                                        {active === "carrier" && ShippingCarrier}
                                        {active === "shipment" && ShipmentSettings}
                                        {active === "shipper" && ShipperAddress}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Page-scoped styles */}
            <style>{`
        .rc-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
        .rc-tab {
          border: 1px solid #e5e7eb; background: #f8f9fb;
          padding: 8px 14px; border-radius: 10px; font-weight: 600; color: #374151;
          transition: all 0.2s ease;
        }
        .rc-tab:hover { background: #eef2ff; }
        .rc-tab.rc-tab-active { box-shadow: 0 4px 14px rgba(0,0,0,.18); }

        /* Mobile and tablet responsive tabs */
        @media (max-width: 991.98px) {
          .rc-tabs { 
            gap: 4px; 
            overflow-x: auto; 
            flex-wrap: nowrap; 
            padding-bottom: 4px;
          }
          .rc-tab { 
            padding: 6px 10px; 
            font-size: 14px; 
            white-space: nowrap;
            min-width: fit-content;
          }
          .rc-tab-icon { display: none !important; }
          .rc-tab-label { display: none !important; }
          .rc-tab-short-label { display: inline !important; }
        }

        /* Extra small mobile devices */
        @media (max-width: 575.98px) {
          .rc-tabs { gap: 2px; }
          .rc-tab { 
            padding: 5px 8px; 
            font-size: 13px; 
            border-radius: 8px;
          }
        }

        .ups-form-control {
          width: 100%; height: 39px; border-radius: 10px; padding: 10px 12px;
          border-width: 2px; border-style: solid; border-color: #c9ced6; background-color: #f5f6fa;
          outline: none; transition: all .2s ease-in-out; box-shadow: none; color: #111827;
        }
        .ups-form-control:hover { border-color: #9ca3af; background-color: #fafafa; }
        .ups-form-control:focus { border-color: #9ca3af; background-color: #fafafa; box-shadow: none; }
        .ups-form-control.is-invalid { border-color: #dc3545; }

        /* React Select (prefix "rs") */
        .rs__control { border-radius: 12px; min-height: 44px; border-width: 2px; border-color: #c9ced6; background-color: #f5f6fa; }
        .rs__control:hover { border-color: #9ca3af; background-color: #fafafa; }
        .rs__value-container { padding: 6px 10px; }

         /* Package cards */
         .pkg-card { background: #fff; border-color: #e5e7eb !important; }
         .pkg-card .form-label { font-size: 0.9rem; }
         
         /* Delete button hover effect */
         .delete-btn {
           transition: all 0.2s ease;
         }
         .delete-btn:hover {
           color: #fb2c36 !important;
           background-color: #e9ebef !important;
           border-radius: 4px;
         }

         /* Shipping table styling */
         .shipping-table {
           border: 1px solid #e5e7eb;
           border-radius: 8px;
           overflow: hidden;
         }
         .shipping-table th,
         .shipping-table td {
           border: none;
         }
         .shipping-table thead th {
           border-bottom: 1px solid #e5e7eb;
         }
           /* rounded container + zebra rows */
.carrier-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.carrier-thead th {
  background: #f5f7fb !important;
  border-bottom: 1px solid #e5e7eb !important;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .02em;
  color: #111827;
}
.carrier-card tbody tr:nth-child(odd)  { background-color: #ffffff; }
.carrier-card tbody tr:nth-child(even) { background-color: #fafbff; }
.carrier-card tbody td { border-top: 1px solid #eef2f6; }

/* bold service code */
.svc-code {
  font-weight: 700;
  color: #4b5563;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

/* wider amount input + compact unit select */
.carrier-amount { width: 220px !important; }
.carrier-unit-select .rs__control {
  min-width: 66px;
  width: 66px;
  height: 39px;
}
      `}</style>
        </>
    );
};

export default UpsConnection;